import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Archive, Loader2, Undo2 } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");

export function CloseOldEnquiries({ onChanged }) {
  const [days, setDays] = useState(90);
  const [d, setD] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setD(null);
    api.get("/enquiry-outcomes/stale", { params: { days } }).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [days]);
  useEffect(() => { load(); setConfirm(false); }, [load]);
  if (d?.error) return null;
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/enquiry-outcomes/close-stale", { days, expected_count: d.count });
      toast.success(`${r.data.updated} enquiries marked Abandoned`);
      setConfirm(false); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not close enquiries"); load(); }
    finally { setBusy(false); }
  };
  const undo = async () => {
    setBusy(true);
    try {
      const r = await api.post("/enquiry-outcomes/undo-close", { batch_id: d.last_batch.batch_id });
      toast.success(`${r.data.restored} enquiries restored`);
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not undo"); }
    finally { setBusy(false); }
  };
  return (
    <section className="bg-white border border-slate-200 rounded-[8px] p-5 space-y-3" data-testid="close-old-enquiries">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Archive className="w-4 h-4 text-slate-500" /> Close old enquiries</h2>
          <p className="text-xs text-slate-500 mt-0.5">Marks enquiries still in Enquiries, not Won or Abandoned, with no activity for the chosen period as Abandoned. They stay in Enquiries and can be restored.</p>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-10 rounded-[8px] border border-slate-200 bg-white px-3 text-sm" data-testid="coe-days">
          {[60, 90, 180, 365].map((n) => <option key={n} value={n}>Older than {n} days</option>)}
        </select>
      </div>
      {!d ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : (
        <>
          <div className="text-sm text-slate-700"><span className="font-semibold tabular-nums" data-testid="coe-count">{d.count}</span> enquiries match.</div>
          {d.sample?.length > 0 && (
            <ul className="text-xs text-slate-500 divide-y divide-slate-100 border border-slate-100 rounded-[8px] max-h-48 overflow-y-auto" data-testid="coe-sample">
              {d.sample.map((s) => <li key={s.legacy_id} className="px-3 py-1.5 flex justify-between gap-3"><span className="truncate text-slate-700">{s.legacy_id} · {s.title || "Untitled"}</span><span className="shrink-0">{fmt(s.created_at)}{s.department_status ? ` · ${s.department_status}` : ""}</span></li>)}
              {d.count > d.sample.length && <li className="px-3 py-1.5 text-slate-400">…and {d.count - d.sample.length} more</li>}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {!confirm ? (
              <button type="button" disabled={!d.count || busy} onClick={() => setConfirm(true)} className="rounded-[8px] h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50" data-testid="coe-start">
                Mark {d.count} as Abandoned
              </button>
            ) : (
              <>
                <span className="text-sm text-amber-700">Mark {d.count} enquiries as Abandoned?</span>
                <button type="button" disabled={busy} onClick={run} className="inline-flex items-center gap-1.5 rounded-[8px] h-10 px-4 bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium" data-testid="coe-confirm">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Yes, mark Abandoned
                </button>
                <button type="button" onClick={() => setConfirm(false)} className="rounded-[8px] h-10 px-4 border border-slate-200 text-sm text-slate-600" data-testid="coe-cancel">Cancel</button>
              </>
            )}
            {d.last_batch && (
              <button type="button" disabled={busy} onClick={undo} className="inline-flex items-center gap-1.5 rounded-[8px] h-10 px-4 border border-slate-200 text-sm text-slate-700 hover:bg-slate-50" data-testid="coe-undo">
                <Undo2 className="w-4 h-4" /> Undo last close ({fmt(d.last_batch.at)})
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
